/**
 * Trunnion AI - Classification Breakdown Chart
 * Donut chart of executions by classification level.
 */

import { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Shield } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { CLASSIFICATION_CONFIG } from "@/config/constants";
import { useDashboardOverview } from "@/hooks/queries/useDashboard";
import type { ClassificationLevel } from "@/types";

interface ClassificationSlice {
  name: string;
  shortLabel: string;
  value: number;
  color: string;
  level: ClassificationLevel;
}

// Mock distribution data until the API provides this breakdown
function generateMockDistribution(
  totalExecutions: number,
): ClassificationSlice[] {
  const weights = {
    UNCLASSIFIED: 0.45,
    CUI: 0.25,
    SECRET: 0.18,
    TOP_SECRET: 0.08,
    TS_SCI: 0.04,
  };

  return (Object.entries(weights) as [ClassificationLevel, number][]).map(
    ([level, weight]) => {
      const config = CLASSIFICATION_CONFIG[level];
      return {
        name: config.label,
        shortLabel: config.shortLabel,
        value: Math.round(totalExecutions * weight),
        color: config.color,
        level,
      };
    },
  );
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    payload: ClassificationSlice;
  }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;

  const { name, value, color } = payload[0].payload;
  return (
    <div className="rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-900 px-3 py-2 text-xs shadow-lg">
      <div className="flex items-center gap-2">
        <span
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="text-white font-medium">{name}</span>
      </div>
      <p className="text-trunnion-slate-400 mt-1">{value} executions</p>
    </div>
  );
}

export function ClassificationBreakdownChart() {
  const { data } = useDashboardOverview();

  const totalExecutions = data?.totalExecutions ?? 0;

  const chartData = useMemo(
    () => generateMockDistribution(totalExecutions || 247),
    [totalExecutions],
  );

  const total = useMemo(
    () => chartData.reduce((sum, d) => sum + d.value, 0),
    [chartData],
  );

  return (
    <Card padding="none">
      <CardHeader>
        <CardTitle>
          <Shield className="h-4 w-4 inline mr-1.5 text-trunnion-gold-500" />
          By Classification
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center">
          {/* Donut chart with center text */}
          <div className="relative h-44 w-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={75}
                  paddingAngle={2}
                  dataKey="value"
                  stroke="none"
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.level} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            {/* Center text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-bold text-white">{total}</span>
              <span className="text-[10px] text-trunnion-slate-400">total</span>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
            {chartData.map((entry) => (
              <div key={entry.level} className="flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-[10px] text-trunnion-slate-400">
                  {entry.shortLabel}
                </span>
                <span className="text-[10px] text-trunnion-slate-500">
                  {entry.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
